import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Upload } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { useToast } from '../../context/ToastContext';
import { validateVariantImageFile, variantTitleFromFile } from '../../utils/workshopVariantUpload';

/** A local image upload, deliberately separate from AI generation and material-library ingestion. */
export default function WorkshopVariantUpload({ assetId, assetName, assetLabel, onUpload, disabled = false }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef(null);
  const submitting = useRef(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const selectFile = (event) => {
    const nextFile = event.target.files?.[0];
    event.target.value = '';
    if (!nextFile) return;
    try {
      validateVariantImageFile(nextFile);
      setFile(nextFile);
      setTitle(current => current.trim() ? current : variantTitleFromFile(nextFile));
      setError('');
    } catch (failure) {
      setError(failure.message);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    try {
      validateVariantImageFile(file);
      if (!title.trim()) throw new Error('请填写变体名称');
    } catch (failure) {
      setError(failure.message);
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError('');
    try {
      await onUpload({ assetId, file, title: title.trim(), description: description.trim() });
      setOpen(false);
      setFile(null);
      setPreview('');
      setTitle('');
      setDescription('');
      toast.success('变体图片已上传并保存');
    } catch (failure) {
      const message = failure?.message || '上传失败，请重试';
      setError(message);
      toast.error(message);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled || saving}
        className="workshop-secondary-action inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold workshop-control-feedback disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
        aria-label={`上传${assetLabel}变体`}
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {saving ? '上传中...' : '上传变体'}
      </button>
      <Modal isOpen={open} onClose={() => setOpen(false)} title={`上传${assetLabel}变体`} width="max-w-md">
        <form onSubmit={submit} className="space-y-4" aria-busy={saving}>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
            为“{assetName || assetLabel}”直接添加图片，不调用 AI。保存后可按当前目标素材库单独入库。
          </p>
          <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={selectFile} disabled={saving} aria-label="选择变体图片" />
          <button
            type="button"
            disabled={saving}
            onClick={() => fileInput.current?.click()}
            className="flex min-h-36 w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-dashed p-3 text-xs transition-colors disabled:opacity-50"
            style={{ borderColor: 'var(--color-border-secondary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}
          >
            {file && preview ? <img src={preview} alt="待上传的变体图片预览" className="max-h-48 max-w-full object-contain" /> : <ImagePlus size={28} />}
            <span className="max-w-full break-all">{file ? file.name : '点击选择变体图片'}</span>
            {file && <span style={{ color: 'var(--color-text-muted)' }}>点击可重新选择</span>}
          </button>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>变体名称</span>
            <input value={title} onChange={event => setTitle(event.target.value)} disabled={saving} required placeholder="例如：日常服装 / 夜景 / 破损版" className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>描述（可选）</span>
            <textarea value={description} onChange={event => setDescription(event.target.value)} disabled={saving} rows={3} placeholder="补充这个变体的服装、状态或场景变化" className="w-full resize-y rounded-lg border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }} />
          </label>
          {error && <p role="alert" className="text-xs leading-relaxed" style={{ color: 'var(--color-error)' }}>{error}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="workshop-secondary-action rounded-lg px-4 py-2 text-xs font-semibold" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
              {saving ? '关闭（后台继续上传）' : '取消'}
            </button>
            <button type="submit" disabled={saving || !file || !title.trim()} className="workshop-primary-action inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50" style={{ backgroundColor: 'var(--color-accent-primary)', color: 'var(--color-accent-contrast, white)' }}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {saving ? '上传并保存中...' : '上传并保存'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
